import React, { type ReactNode } from 'react'
import classNames from 'classnames'

import { TtCard } from '@23/ui-react'

import './VideoPreviewLayout.scss'

interface VideoPageLayoutProps {
  videoPlayer: ReactNode
  title: ReactNode
  description: ReactNode
  logo?: ReactNode
  sidebar?: ReactNode
  className?: string
  /** When true, renders a public standalone preview page. */
  isPreview?: boolean
  cardBackground?: {
    color?: string
    imageUrl?: string
  }
}

export const VideoPreviewLayout = ({
  videoPlayer,
  title,
  description,
  logo,
  sidebar,
  className,
  isPreview = false,
  cardBackground,
}: VideoPageLayoutProps) => {
  const hasSidebar = !!sidebar

  const cardStyle = {
    '--video-page-layout-bg-color':
      cardBackground?.color || 'var(--tt-color-white)',
    '--video-page-layout-bg-image': cardBackground?.imageUrl
      ? `url(${cardBackground.imageUrl})`
      : 'none',
  } as React.CSSProperties

  const rootClassName = classNames('video-page-layout', className, {
    'is-preview': isPreview,
  })

  const body = (
    <div
      className={classNames('video-page-body', {
        'video-page-body--with-logo': !!logo,
        'video-page-body--no-sidebar': !hasSidebar,
      })}
    >
      <div className="video-logo-area">{logo}</div>
      <main className="video-page-main">
        <div className="video-page-content">
          <div className="video-player-wrapper">{videoPlayer}</div>
          <div className="video-page-layout-details">
            <div className="video-title-area">{title}</div>
            {description && (
              <div className="video-description-area">{description}</div>
            )}
          </div>
        </div>
      </main>
      {hasSidebar && <aside className="video-page-sidebar">{sidebar}</aside>}
    </div>
  )

  if (!isPreview) {
    return (
      <TtCard className={rootClassName} style={cardStyle} hideLabel>
        {body}
      </TtCard>
    )
  }

  return (
    <div className={rootClassName} style={cardStyle}>
      {body}
    </div>
  )
}
